"use client";

import React, { useState } from "react";
import { useSession } from "next-auth/react";
import { apiPost } from "@/lib/api";
import { ShieldCheck, Users, User } from "lucide-react";
import { Button } from "@/components/ui/button";

interface SelfReportDialogProps {
  content2257RecordId: number;
  faceCount: number;
  sexualActivityDetected?: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSoloConfirmed: () => void;
  onMultipleConfirmed: (content2257RecordId: number) => void;
}

export function SelfReportDialog({
  content2257RecordId,
  faceCount,
  sexualActivityDetected,
  isOpen,
  onClose,
  onSoloConfirmed,
  onMultipleConfirmed,
}: SelfReportDialogProps) {
  const { data: session } = useSession();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mismatchWarning, setMismatchWarning] = useState(false);

  if (!isOpen) return null;

  async function handleReport(performerCount: 1 | 2) {
    setSubmitting(true);
    setError(null);
    setMismatchWarning(false);
    try {
      const result = await apiPost<{
        content2257RecordId: number;
        aiMismatchDetected: boolean;
        requiresPerformerTagging: boolean;
      }>(`/api/content/${content2257RecordId}/self-report`, session, {
        performerCount,
      });

      if (result.aiMismatchDetected) {
        setMismatchWarning(true);
        setSubmitting(false);
        return;
      }

      if (result.requiresPerformerTagging) {
        onMultipleConfirmed(content2257RecordId);
      } else {
        onSoloConfirmed();
      }
    } catch (e: any) {
      setError(e?.message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[1000] bg-black/60 grid place-items-center p-4">
      <div className="bg-background text-foreground w-full max-w-md rounded-xl border shadow-lg overflow-hidden">
        <div className="p-6 text-center space-y-4">
          <div className="mx-auto w-14 h-14 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-lg font-semibold">
            {sexualActivityDetected && faceCount < 2 ? "Sexual Activity Detected" : "Multiple People Detected"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {sexualActivityDetected && faceCount < 2
              ? "Our system detected sexual activity in this content. All performers must be tagged and provide consent."
              : `Our system detected ${faceCount} ${faceCount === 1 ? "person" : "people"} in this content.`}
            {" "}Who appears in this content?
          </p>
          {error && <p className="text-sm text-red-600">{error}</p>}
          {mismatchWarning && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <p className="text-sm text-amber-800 dark:text-amber-200">
                You indicated only you appear, but our AI detected multiple people.
                This content has been flagged for admin review. You can continue uploading other content.
              </p>
            </div>
          )}
        </div>

        {mismatchWarning ? (
          <div className="px-6 pb-6">
            <Button
              variant="outline"
              className="w-full"
              onClick={onClose}
            >
              Got It
            </Button>
          </div>
        ) : (
          <div className="px-6 pb-6 flex flex-col gap-2">
            <Button
              className="w-full justify-start gap-3"
              disabled={submitting}
              onClick={() => handleReport(1)}
            >
              <User className="w-5 h-5 shrink-0" />
              <span>Just me</span>
            </Button>
            <Button
              variant="outline"
              className="w-full justify-start gap-3"
              disabled={submitting}
              onClick={() => handleReport(2)}
            >
              <Users className="w-5 h-5 shrink-0" />
              <span>Me and another JuicyStarz member</span>
            </Button>
            <Button
              variant="ghost"
              className="w-full mt-1"
              disabled={submitting}
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SelfReportDialog;
